import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  styled,
  Card,
  Snackbar,
  Alert as MuiAlert,
  CircularProgress
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '@/data/authService';
import { useTranslation } from 'react-i18next';
import OtpVerifyModal from '@/components/OtpVerifyModal';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  StyledPhoneInput,
  PhoneInputError
} from '@/components/StyledPhoneInput';

const MainContent = styled(Box)(
  () => `
    height: 100%;
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-top: 50px;
`
);

const ForgotPasswordCard = styled(Card)(
  ({ theme }) => `
    padding: ${theme.spacing(4)};
    margin: ${theme.spacing(2)};
    width: 100%;
    max-width: 420px;
`
);

function ForgotPassword() {
  const [phone, setPhone] = useState<string | undefined>('');
  const [otp, setOtp] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isChangeable, setIsChangeable] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isPhoneValid = () => (phone ? isPossiblePhoneNumber(phone) : false);

  // Step 1: forgot-password endpoint hem kullanıcı var mı kontrol eder hem de
  // kayıtlı telefona SMS OTP gönderir. OTP doğrulaması artık sunucu tarafında.
  const handleSendOtp = async () => {
    setError(undefined);

    if (!phone || !isPossiblePhoneNumber(phone)) {
      setError(t('please.enter.valid.phone.number'));
      return;
    }

    try {
      setLoading(true);
      const fpResult = await authService.forgotPassword({ phone });

      if (fpResult.status !== 200) {
        setError(fpResult.message || t('something.went.wrong'));
        return;
      }

      if (fpResult.isExists === false) {
        setError(t('phone.not.registered'));
        return;
      }

      setShowOtpModal(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || t('something.went.wrong'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: girilen kodu sunucuda ön-kontrol et (tüketmeden).
  const handleOtpConfirm = async (enteredCode: string) => {
    setError(undefined);

    if (!phone) return;

    try {
      setLoading(true);
      const result = await authService.verifyOtp({ phone, otp: enteredCode });

      if (result.valid) {
        setOtp(enteredCode);
        setShowOtpModal(false);
        setIsChangeable(true);
      } else {
        setError(t('invalid.verification.code'));
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || t('something.went.wrong'));
    } finally {
      setLoading(false);
    }
  };

  // Step 3: yeni şifreyi otp ile birlikte gönder; sunucu OTP'yi doğrular + tüketir.
  const handlePasswordChange = async () => {
    setError(undefined);

    if (!newPassword || !confirmPassword) {
      setError(t('please.fill.all.fields'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('password.min.length'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('passwords.do.not.match'));
      return;
    }

    if (!phone) return;

    try {
      setLoading(true);
      const result = await authService.changePassword({
        phone,
        newPassword,
        otp
      });

      if (result.status !== 200) {
        setError(result.message || t('something.went.wrong'));
        setLoading(false);
        return;
      }

      setSuccessMessage(t('password.changed.successfully'));
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/auth/login');
      }, 2000);
    } catch (e: any) {
      setError(e?.response?.data?.message || t('something.went.wrong'));
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('forgot.password')}</title>
      </Helmet>
      <MainContent>
        <Container maxWidth="sm">
          <ForgotPasswordCard>
            <Typography variant="h4" align="center" gutterBottom>
              {t('forgot.password')}
            </Typography>

            {!isChangeable && (
              <>
                <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                  {t('forgot.password.phone.prompt')}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <StyledPhoneInput
                    defaultCountry="TR"
                    value={phone}
                    onChange={setPhone}
                    placeholder={t('phone')}
                    error={
                      phone && !isPossiblePhoneNumber(phone)
                        ? t('invalid.phone.number')
                        : undefined
                    }
                  />
                  {phone && !isPossiblePhoneNumber(phone) && (
                    <PhoneInputError role="alert">
                      {t('invalid.phone.number')}
                    </PhoneInputError>
                  )}
                </Box>
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={handleSendOtp}
                  disabled={loading || !isPhoneValid()}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('send.verification.code')
                  )}
                </Button>
              </>
            )}

            {isChangeable && (
              <>
                <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                  {t('set.new.password')}
                </Typography>
                <TextField
                  fullWidth
                  label={t('new.password')}
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t('confirm.new.password')}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={handlePasswordChange}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('change.password')
                  )}
                </Button>
              </>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link to="/auth/login">{t('back.to.login')}</Link>
            </Box>
          </ForgotPasswordCard>
        </Container>
      </MainContent>

      <OtpVerifyModal
        visible={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onConfirm={handleOtpConfirm}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <MuiAlert
          variant="filled"
          severity="success"
          onClose={() => setShowSuccess(false)}
        >
          <Typography>{successMessage}</Typography>
        </MuiAlert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(undefined)}
      >
        <MuiAlert
          variant="filled"
          severity="error"
          onClose={() => setError(undefined)}
        >
          <Typography>{error}</Typography>
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default ForgotPassword;
